import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import styles from "./Commissions.module.css";

const fmt = (n) =>
  `KES ${Number(n ?? 0).toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;

const COMMISSION_RATE = 0.20;

// ── Helpers for week calculation ──────────────────────────
function getWeekRange(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

const toISO = (d) => d.toISOString().slice(0, 10);

export default function Commissions() {
  const { isAdmin, profile } = useAuth();
  const [clients,    setClients]    = useState([]);
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState([]); // client ids chosen for new request
  const [submitting, setSubmitting] = useState(false);
  const [reviewing,  setReviewing]  = useState(null); // request being reviewed (admin)
  const [reviewItems,setReviewItems]= useState([]);
  const [actionErr,  setActionErr]  = useState("");
  const [tab,        setTab]        = useState("eligible"); // eligible | history

  useEffect(() => { if (profile) load(); }, [profile]); // eslint-disable-line

  async function load() {
    setLoading(true);

    // All commission_items ever created (to know what's already claimed)
    const { data: claimedItems } = await supabase
      .from("commission_items")
      .select("client_id, request:commission_requests(status)");

    const claimedClientIds = new Set(
      (claimedItems ?? [])
        .filter((i) => i.request?.status !== "rejected") // rejected = re-claimable
        .map((i) => i.client_id)
    );

    // Clients with payments, scoped to role
    let cq = supabase
      .from("clients")
      .select("*, consultant:employees(id, full_name)")
      .gt("amount_received", 0)
      .order("date_started", { ascending: false });

    if (!isAdmin) cq = cq.eq("consultant_id", profile.id);

    const { data: clientData } = await cq;
    const eligible = (clientData ?? []).filter((c) => !claimedClientIds.has(c.id));
    setClients(eligible);

    // Requests
    let rq = supabase
      .from("commission_requests")
      .select("*, consultant:employees!commission_requests_consultant_id_fkey(full_name), items:commission_items(*, client:clients(full_name, phone))")
      .order("created_at", { ascending: false });

    if (!isAdmin) rq = rq.eq("consultant_id", profile.id);

    const { data: reqData } = await rq;
    setRequests(reqData ?? []);

    setLoading(false);
  }

  function toggleSelect(clientId) {
    setSelected((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  }

  const selectedClients = clients.filter((c) => selected.includes(c.id));
  const selectedTotal   = selectedClients.reduce((s, c) => s + Number(c.amount_received), 0);
  const commissionTotal = selectedTotal * COMMISSION_RATE;

  async function submitRequest() {
    if (selected.length === 0) return;
    setSubmitting(true);
    setActionErr("");

    const { start, end } = getWeekRange();

    const { data: req, error: reqErr } = await supabase
      .from("commission_requests")
      .insert({
        consultant_id: profile.id,
        week_start: toISO(start),
        week_end: toISO(end),
        total_amount: commissionTotal,
        status: "pending",
      })
      .select()
      .single();

    if (reqErr) { setActionErr(reqErr.message); setSubmitting(false); return; }

    const items = selectedClients.map((c) => ({
      request_id: req.id,
      client_id: c.id,
      payment_amount: c.amount_received,
      commission_rate: COMMISSION_RATE,
    }));

    const { error: itemsErr } = await supabase.from("commission_items").insert(items);

    setSubmitting(false);
    if (itemsErr) { setActionErr(itemsErr.message); return; }

    setSelected([]);
    setTab("history");
    load();
  }

  async function reviewRequest(req) {
    setReviewing(req);
    setReviewItems(req.items ?? []);
  }

  async function updateRequestStatus(status) {
    setActionErr("");
    const { error } = await supabase
      .from("commission_requests")
      .update({
        status,
        reviewed_by: profile.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", reviewing.id);

    if (error) { setActionErr(error.message); return; }
    setReviewing(null);
    load();
  }

  const TABS = [
    { key: "eligible", label: isAdmin ? "💰 Unclaimed Payments" : "💰 My Unclaimed Payments" },
    { key: "history",  label: "📋 Requests" + (isAdmin ? " (All)" : "") },
  ];

  if (loading) return <div className={styles.loading}>Loading commissions…</div>;

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className={styles.page}>
      {/* ── Summary cards ── */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Unclaimed Payments</div>
          <div className={styles.summaryValue}>{clients.length}</div>
          <div className={styles.summarySub}>
            {fmt(clients.reduce((s,c)=>s+Number(c.amount_received),0))} total received
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.green}`}>
          <div className={styles.summaryLabel}>Potential Commission</div>
          <div className={styles.summaryValue}>
            {fmt(clients.reduce((s,c)=>s+Number(c.amount_received),0) * COMMISSION_RATE)}
          </div>
          <div className={styles.summarySub}>at 20% rate</div>
        </div>
        <div className={`${styles.summaryCard} ${styles.orange}`}>
          <div className={styles.summaryLabel}>Pending Requests</div>
          <div className={styles.summaryValue}>{pendingCount}</div>
          <div className={styles.summarySub}>awaiting review</div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.key}
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Eligible payments tab ── */}
      {tab === "eligible" && (
        <div className={styles.card}>
          {clients.length === 0 ? (
            <div className={styles.empty}>No unclaimed payments right now.</div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>Client</th>
                      {isAdmin && <th>Consultant</th>}
                      <th>Service</th>
                      <th>Amount Received</th>
                      <th>Commission (20%)</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id}
                        className={selected.includes(c.id) ? styles.rowSelected : ""}
                        onClick={() => toggleSelect(c.id)}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.includes(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td><strong>{c.full_name}</strong><div className={styles.muted}>{c.phone}</div></td>
                        {isAdmin && <td>{c.consultant?.full_name ?? "—"}</td>}
                        <td style={{ fontSize: 12.5 }}>{c.service_type}</td>
                        <td className={styles.green}>{fmt(c.amount_received)}</td>
                        <td><strong>{fmt(c.amount_received * COMMISSION_RATE)}</strong></td>
                        <td style={{ fontSize: 12 }}>{c.date_started ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Eligible Payment Cards */}
                <div className={styles.mobileList}>
                  {clients.map((c) => {
                    const isSelected = selected.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        className={`${styles.eligibleCard} ${isSelected ? styles.eligibleCardSelected : ""}`}
                        onClick={() => toggleSelect(c.id)}
                      >
                        <div className={styles.eligibleCardTop}>
                          <div className={styles.checkboxWrap}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(c.id)}
                              onClick={(e) => e.stopPropagation()}
                              className={styles.mobileCheckbox}
                            />
                            <div className={styles.eligibleClientInfo}>
                              <div className={styles.eligibleName}>{c.full_name}</div>
                              <div className={styles.eligiblePhone}>{c.phone}</div>
                            </div>
                          </div>
                          <span className={styles.eligibleDate}>{c.date_started ?? "—"}</span>
                        </div>

                        <div className={styles.eligibleDetails}>
                          <div className={styles.eligibleRow}>
                            <span className={styles.eligibleLabel}>Service:</span>
                            <span className={styles.eligibleVal}>{c.service_type}</span>
                          </div>
                          {isAdmin && c.consultant?.full_name && (
                            <div className={styles.eligibleRow}>
                              <span className={styles.eligibleLabel}>Consultant:</span>
                              <span className={styles.eligibleVal}>{c.consultant.full_name}</span>
                            </div>
                          )}
                        </div>

                        <div className={styles.eligibleFinances}>
                          <div className={styles.eligibleFinCol}>
                            <span className={styles.finLabel}>Received</span>
                            <span className={`${styles.finVal} ${styles.green}`}>{fmt(c.amount_received)}</span>
                          </div>
                          <div className={styles.eligibleFinCol}>
                            <span className={styles.finLabel}>Commission (20%)</span>
                            <span className={`${styles.finVal} ${styles.commVal}`}>
                              {fmt(c.amount_received * COMMISSION_RATE)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!isAdmin && (
                <div className={styles.submitBar}>
                  <div className={styles.submitInfo}>
                    <strong>{selected.length}</strong> selected ·
                    Payments: <strong>{fmt(selectedTotal)}</strong> ·
                    Commission: <strong className={styles.green}>{fmt(commissionTotal)}</strong>
                  </div>
                  <button
                    className={styles.submitBtn}
                    disabled={selected.length === 0 || submitting}
                    onClick={submitRequest}
                  >
                    {submitting ? "Submitting…" : "Submit Commission Request"}
                  </button>
                </div>
              )}
              {actionErr && <div className={styles.err}>{actionErr}</div>}
            </>
          )}
        </div>
      )}

      {/* ── Requests history tab ── */}
      {tab === "history" && (
        <div className={styles.card}>
          {requests.length === 0 ? (
            <div className={styles.empty}>No commission requests yet.</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {isAdmin && <th>Consultant</th>}
                    <th>Week</th>
                    <th>Clients</th>
                    <th>Commission Amount</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id}>
                      {isAdmin && <td><strong>{r.consultant?.full_name ?? "—"}</strong></td>}
                      <td style={{ fontSize: 12.5 }}>{r.week_start} → {r.week_end}</td>
                      <td>{r.items?.length ?? 0}</td>
                      <td><strong>{fmt(r.total_amount)}</strong></td>
                      <td><Badge label={r.status} /></td>
                      <td style={{ fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString("en-KE")}</td>
                      <td>
                        <button className={styles.viewBtn} onClick={() => reviewRequest(r)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Request Cards */}
              <div className={styles.mobileList}>
                {requests.map((r) => (
                  <div key={r.id} className={styles.requestCard}>
                    <div className={styles.requestHeader}>
                      <div>
                        {isAdmin && (
                          <div className={styles.requestConsultant}>
                            👤 {r.consultant?.full_name ?? "—"}
                          </div>
                        )}
                        <div className={styles.requestWeek}>
                          📅 {r.week_start} → {r.week_end}
                        </div>
                      </div>
                      <Badge label={r.status} />
                    </div>

                    <div className={styles.requestBody}>
                      <div className={styles.requestStat}>
                        <span className={styles.requestStatLabel}>Clients Included:</span>
                        <span className={styles.requestStatVal}>{r.items?.length ?? 0}</span>
                      </div>
                      <div className={styles.requestStat}>
                        <span className={styles.requestStatLabel}>Total Commission:</span>
                        <span className={`${styles.requestStatVal} ${styles.green}`}>
                          {fmt(r.total_amount)}
                        </span>
                      </div>
                      <div className={styles.requestStat}>
                        <span className={styles.requestStatLabel}>Submitted:</span>
                        <span className={styles.requestStatVal}>
                          {new Date(r.created_at).toLocaleDateString("en-KE")}
                        </span>
                      </div>
                    </div>

                    <div className={styles.requestFooter}>
                      <button
                        className={styles.viewBtnMobile}
                        onClick={() => reviewRequest(r)}
                      >
                        View Details →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Review modal ── */}
      {reviewing && (
        <Modal
          title={`Commission Request — ${reviewing.week_start} to ${reviewing.week_end}`}
          onClose={() => setReviewing(null)}
          width={620}
        >
          <div className={styles.modalBody}>
            {isAdmin && (
              <div className={styles.modalConsultant}>
                Consultant: <strong>{reviewing.consultant?.full_name}</strong>
              </div>
            )}

            <div className={styles.itemsList}>
              {reviewItems.map((item) => (
                <div key={item.id} className={styles.itemRow}>
                  <div>
                    <strong>{item.client?.full_name ?? "—"}</strong>
                    <div className={styles.muted}>{item.client?.phone}</div>
                  </div>
                  <div className={styles.itemAmounts}>
                    <div>{fmt(item.payment_amount)} paid</div>
                    <div className={styles.green}><strong>{fmt(item.commission_amount)}</strong></div>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.modalTotal}>
              Total Commission: <strong>{fmt(reviewing.total_amount)}</strong>
            </div>

            <div className={styles.modalStatus}>
              Status: <Badge label={reviewing.status} />
            </div>

            {actionErr && <div className={styles.err}>{actionErr}</div>}

            {isAdmin && reviewing.status === "pending" && (
              <div className={styles.modalActions}>
                <button className={styles.rejectBtn} onClick={() => updateRequestStatus("rejected")}>
                  Reject
                </button>
                <button className={styles.approveBtn} onClick={() => updateRequestStatus("approved")}>
                  Approve
                </button>
              </div>
            )}

            {isAdmin && reviewing.status === "approved" && (
              <div className={styles.modalActions}>
                <button className={styles.paidBtn} onClick={() => updateRequestStatus("paid")}>
                  Mark as Paid
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
